import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useEventContext } from "Bands/contexts";
import {
  clearDataByKey,
  dataMetaSelector,
  getEventGoogleLocations,
  getEventSuggestedLocations,
  googleLocationsSelector,
  suggestedLocationsSelector,
} from "Bands/state";
import { BAND_ACTIONS, EventBlocks, IEventSuggestedLocation } from "Bands/types";
import { getSuggestedLocationsOptions, useBandRegionalSettings } from "Bands/utils";
import { ISelectOption } from "shared/components/Form/Fields/select/types";
import { IAPIState, IDataKeys } from "shared/types";

export const useAccommodationBlockLocationOptions = (
  defaultSuggestedLocations?: IEventSuggestedLocation[]
): {
  options: ISelectOption[];
  resetLocations: () => void;
  fetchGoogleLocations: (term: string) => void;
  isFetchingLocations: boolean;
} => {
  const dispatch = useDispatch();
  const { dateFormat } = useBandRegionalSettings();
  const { eventId } = useEventContext();

  const suggestedLocations = useSelector(suggestedLocationsSelector);
  const googleLocations = useSelector(googleLocationsSelector);

  const { api: suggestedMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventSuggestedLocations)
  );
  const { api: googleMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.getEventGoogleLocations));

  // fetch suggested locations on mount
  useEffect(() => {
    if (!eventId) return;

    dispatch(
      getEventSuggestedLocations({ event: eventId, eventBlockType: EventBlocks.accommodation })
    );
  }, [dispatch, eventId]);

  const fetchGoogleLocations = useCallback(
    (inputValue: string) => {
      dispatch(
        getEventGoogleLocations({ term: inputValue, eventBlockType: EventBlocks.accommodation })
      );
    },
    [dispatch]
  );

  const resetLocations = () => {
    dispatch(clearDataByKey(IDataKeys.eventGoogleLocation));
  };

  const suggestedLocationOptions = useMemo(
    () =>
      getSuggestedLocationsOptions({
        locations: suggestedLocations || defaultSuggestedLocations,
        dateFormat,
      }),
    [dateFormat, suggestedLocations, defaultSuggestedLocations]
  );

  const googleLocationOptions = useMemo(
    () =>
      getSuggestedLocationsOptions({
        locations: googleLocations,
        dateFormat,
        alwaysShow: true,
      }),
    [dateFormat, googleLocations]
  );

  return {
    options: [...suggestedLocationOptions, ...googleLocationOptions],
    resetLocations,
    fetchGoogleLocations,
    isFetchingLocations:
      suggestedMeta?.status === IAPIState.pending || googleMeta?.status === IAPIState.pending,
  };
};
